import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [logoutBlockedModal, setLogoutBlockedModal] = useState(false);
  const tasksChecked = useRef(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('ats_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('ats_user');
        localStorage.removeItem('ats_token');
      }
    }
    setLoading(false);
  }, []);

  // Check for pending tasks on user load and periodically
  useEffect(() => {
    if (user && (user.role === 'recruiter' || user.role === 'hr_manager')) {
      checkPendingTasks();
      const interval = setInterval(checkPendingTasks, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user]);

  const checkPendingTasks = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      if (!token) return [];
      
      const response = await fetch(`${API_URL}/api/todos?status=pending&status=in_progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter urgent/high priority tasks that must be completed
        const urgentTasks = (data.todos || []).filter(t => 
          t.priority === 'urgent' || t.priority === 'high'
        );
        setPendingTasks(urgentTasks);
        tasksChecked.current = true;
        return urgentTasks;
      }
      return [];
    } catch (error) {
      console.error('Failed to check pending tasks:', error);
      return [];
    }
  };

  const login = useCallback((userData, token) => {
    localStorage.setItem('ats_token', token);
    localStorage.setItem('ats_user', JSON.stringify(userData));
    setUser(userData);
    tasksChecked.current = false; // Reset for fresh check
  }, []);

  const logout = useCallback(async (forceOverride = false, overrideReason = '') => {
    // For recruiters/hr_managers, always check tasks fresh before logout
    if (!forceOverride && (user?.role === 'recruiter' || user?.role === 'hr_manager')) {
      // Fetch fresh tasks before deciding
      const freshTasks = await checkPendingTasks();
      const tasksToCheck = freshTasks.length > 0 ? freshTasks : pendingTasks;
      
      if (tasksToCheck.length > 0) {
        setPendingTasks(tasksToCheck);
        setLogoutBlockedModal(true);
        return false; // Logout blocked
      }
    }

    // If force override (by senior), log the override
    if (forceOverride && overrideReason) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        await fetch(`${API_URL}/api/activity-log`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'logout_override',
            details: {
              pending_tasks: pendingTasks.length,
              reason: overrideReason,
              user_id: user?.id,
              user_name: user?.full_name
            }
          })
        });
      } catch (error) {
        console.error('Failed to log override:', error);
      }
    }

    localStorage.removeItem('ats_token');
    localStorage.removeItem('ats_user');
    setUser(null);
    setPendingTasks([]);
    setLogoutBlockedModal(false);
    tasksChecked.current = false;
    return true;
  }, [pendingTasks, user]);

  const hasRole = useCallback((...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  const canAccess = useCallback((minRole) => {
    if (!user) return false;
    const hierarchy = {
      'super_admin': 4,
      'admin': 3,
      'hr_manager': 2,
      'recruiter': 1,
    };
    return hierarchy[user.role] >= hierarchy[minRole];
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      hasRole, 
      canAccess,
      pendingTasks,
      logoutBlockedModal,
      setLogoutBlockedModal,
      checkPendingTasks
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
